import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Export data as Excel (.xlsx)
 * @param {Array} rows - Array of flat objects
 * @param {Array} columns - [{header, key}]  
 * @param {string} filename - without extension
 * @param {string} sheetName
 */
export const exportToExcel = (rows, columns, filename, sheetName = 'Sheet1') => {
  const headers = columns.map(c => c.header);
  const data = rows.map(row => columns.map(c => {
    const val = typeof c.key === 'function' ? c.key(row) : row[c.key];
    return val ?? '';
  }));

  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

  // Column widths
  ws['!cols'] = columns.map(c => ({ wch: Math.max(c.header.length + 2, 14) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export data as PDF
 * @param {Array} rows - Array of flat objects
 * @param {Array} columns - [{header, key}]
 * @param {string} filename - without extension
 * @param {string} title - Page title
 * @param {string} subtitle - optional subtitle (e.g. date range)
 */
export const exportToPDF = (rows, columns, filename, title, subtitle = '') => {
  const doc = new jsPDF({ orientation: columns.length > 7 ? 'landscape' : 'portrait' });

  // Header block
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('LSN Minerals', 14, 16);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 14, 24);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(subtitle, 14, 30);
    doc.setTextColor(0);
  }

  const tableColumns = columns.map(c => c.header);
  const tableRows = rows.map(row =>
    columns.map(c => {
      const val = typeof c.key === 'function' ? c.key(row) : row[c.key];
      return val !== null && val !== undefined ? String(val) : '—';
    })
  );

  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: subtitle ? 36 : 30,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [30, 30, 30], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 14, right: 14 },
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-IN')} | Page ${i} of ${pageCount}`,
      14,
      doc.internal.pageSize.height - 8
    );
  }

  doc.save(`${filename}.pdf`);
};

/**
 * ExportButton component — renders a dropdown with Excel + PDF options
 */
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Download, FileSpreadsheet, FileText, ChevronDown } from 'lucide-react';

export const ExportButton = ({ rows, columns, filename, title, subtitle, disabled }) => {
  const [open, setOpen] = useState(false);

  if (!rows || rows.length === 0) return null;

  return (
    <div className="relative inline-block">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        data-testid="export-btn"
        className="gap-1.5"
      >
        <Download className="h-3.5 w-3.5" />
        Export
        <ChevronDown className="h-3 w-3" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              onClick={() => { exportToExcel(rows, columns, filename, title); setOpen(false); }}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
              Export as Excel
            </button>
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              onClick={() => { exportToPDF(rows, columns, filename, title, subtitle); setOpen(false); }}
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 text-red-600" />
              Export as PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
};
